import { DEFAULT_MONEY_SCALE, roundMoney } from '../money';

export type PriceListPricingMode = 'fixed' | 'percent_off_base';

export type PriceListItemLike = {
  productId: string;
  variantId?: string | null;
  price?: number | null;
  percentOff?: number | null;
  minQty?: number | null;
};

export type PriceListLike = {
  id: string;
  pricingMode: PriceListPricingMode;
  isActive?: boolean;
};

export type ResolveEffectiveUnitPriceInput = {
  basePrice: number;
  productId: string;
  variantId?: string | null;
  quantity?: number;
  priceList?: PriceListLike | null;
  items?: PriceListItemLike[] | null;
};

function roundListedPrice(value: number): number {
  return roundMoney(value, DEFAULT_MONEY_SCALE);
}

function findMatchingItem(
  items: PriceListItemLike[],
  productId: string,
  variantId: string | null | undefined,
  quantity: number,
): PriceListItemLike | null {
  const variantKey = variantId ?? '';
  const candidates = items.filter((item) => {
    if (item.productId !== productId) return false;
    const itemVariant = item.variantId ?? '';
    if (itemVariant !== variantKey) return false;
    const minQty = item.minQty ?? 0;
    return quantity >= minQty;
  });
  if (candidates.length === 0) return null;
  return candidates.reduce((best, item) => {
    const bestMin = best.minQty ?? 0;
    const itemMin = item.minQty ?? 0;
    return itemMin >= bestMin ? item : best;
  });
}

/**
 * Precio unitario efectivo antes de promos.
 * Precedencia: ítem de lista (fixed o % sobre base) → precio catálogo.
 */
export function resolveEffectiveUnitPrice(input: ResolveEffectiveUnitPriceInput): number {
  const basePrice = input.basePrice;
  if (!Number.isFinite(basePrice) || basePrice < 0) return 0;

  const list = input.priceList;
  if (!list || list.isActive === false) return roundListedPrice(basePrice);

  const items = input.items ?? [];
  const match = findMatchingItem(
    items,
    input.productId,
    input.variantId,
    input.quantity ?? 1,
  );
  if (!match) return roundListedPrice(basePrice);

  if (match.price != null && Number.isFinite(match.price)) {
    return roundListedPrice(match.price);
  }

  const percentOff = match.percentOff;
  if (percentOff != null && Number.isFinite(percentOff)) {
    return roundListedPrice(basePrice * (1 - percentOff / 100));
  }

  if (list.pricingMode === 'percent_off_base' && match.percentOff == null && match.price == null) {
    return roundListedPrice(basePrice);
  }

  return roundListedPrice(basePrice);
}
